import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CustomError } from 'pricing-pages-lib/errors/CustomErrors';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { useRef } from 'react';
import { NavMarker } from 'react-rhumb';
import { CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE, HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { NavItemEnum } from '../../../constants/routing/NavItems';
import { useErrorBoundary } from '../../error-boundary/useErrorBoundary';
import withErrorBoundary from '../../error-boundary/withErrorBoundary';
import { PinnedFeaturesConfig } from '../PinnedFeaturesConfig';
import SuiteFeatureTableHeader from './SuiteFeatureTableHeader';
import SuiteFeatureTableHeaderCell from './SuiteFeatureTableHeaderCell';
import { usePinnedFeaturesByProductLine } from '../../../hooks/useFeatures';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const buildSuiteFeatureTableHeaderData = (levels, isFreeToStarter = false, screen) => {
  const matchApiNameByLevel = {
    FREE: HUBSPOT_FREE,
    STARTER: CRM_SUITE_STARTER_BUNDLE,
    PROFESSIONAL: CRM_SUITE_PROFESSIONAL_BUNDLE,
    ENTERPRISE: CRM_SUITE_ENTERPRISE_BUNDLE
  };
  return levels.reduce((headers, level) => {
    headers[matchApiNameByLevel[level]] = {
      Content: /*#__PURE__*/_jsx(SuiteFeatureTableHeaderCell, {
        productLevel: level,
        isFreeToStarter: isFreeToStarter,
        screen: screen
      }, level)
    };
    return headers;
  }, {});
};
const buildSuiteFeatureTableBodyData = ({
  features,
  subscreen,
  productLines,
  apiNames,
  staticDisplayRowCount
}) => {
  return {
    sharedData: {
      subscreen,
      staticDisplayRowCount
    },
    sectionData: productLines.reduce((acc, productLine) => {
      acc[productLine] = {
        apiNames: apiNames[productLine],
        features: features[productLine] || [],
        LabelContent: /*#__PURE__*/_jsx(SuiteFeatureTableHeader, {
          productLine: productLine
        })
      };
      return acc;
    }, {})
  };
};
const SuiteFeatureTable = ({
  productsByProductLine,
  isFreeToStarter,
  screen
}) => {
  var _PinnedFeaturesConfig;
  const {
    showBoundary
  } = useErrorBoundary();
  const {
    featuresByProductLine,
    loading,
    error
  } = usePinnedFeaturesByProductLine(productsByProductLine);
  const productLines = Object.keys(featuresByProductLine);
  const featureTableRef = useRef(null);
  const sortedProductLines = sortProductLines(productLines);
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  });
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError('Error fetching features', {
      error,
      productsByProductLine
    }));
  }
  if (!featuresByProductLine || !Object.keys(featuresByProductLine).length) {
    return null;
  }
  const tableHeader = buildSuiteFeatureTableHeaderData([...levels], isFreeToStarter, screen);
  const tableBody = buildSuiteFeatureTableBodyData({
    features: featuresByProductLine,
    subscreen: NavItemEnum.SUITE,
    productLines: sortedProductLines,
    apiNames: productsByProductLine,
    staticDisplayRowCount: Math.min(((_PinnedFeaturesConfig = PinnedFeaturesConfig[MARKETING]) === null || _PinnedFeaturesConfig === void 0 ? void 0 : _PinnedFeaturesConfig.length) || 5, 5)
  });
  if (!tableBody) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_FEATURE_TABLE"
    }), /*#__PURE__*/_jsx("div", {
      ref: featureTableRef,
      "data-selenium": "suite-feature-table",
      id: "feature-table",
      children: /*#__PURE__*/_jsx(FeatureTable, {
        headerData: tableHeader,
        bodyData: tableBody,
        canViewNewSeats: false // does not apply to free and suite products, but a value is required
      })
    })]
  });
};
export default withErrorBoundary(SuiteFeatureTable, {
  fallback: null,
  errorBoundaryName: 'SUITE_FEATURE_TABLE'
});